import type { CSSProperties, ReactNode } from 'react';

import type { StorefrontCategory } from '@/features/storefront/api/storefront';
import { StorefrontContainer as Container } from '@/features/storefront/components/StorefrontContainer';
import type { LinkDirectorySection } from '@/features/storefront/sections/schema';
import { cn } from '@/lib/cn';

/**
 * A dense directory of grouped text links - the "popular searches" block a
 * grocery marketplace keeps near the foot of its home page: a short bold
 * heading per department, then an inline run of small links to what is in it.
 *
 * From the shop's own categories by default (each top-level category a
 * heading, its sub-categories the links), or the merchant's own groups.
 */

/** One link in a group, as a shop path ("/categories/tea"). */
export interface DirectoryLink {
  label: string;
  href: string;
}

/** One group as drawn: a heading (maybe a link itself) and its run of links. */
export interface DirectoryGroup {
  id: string;
  heading: string;
  headingHref?: string;
  links: DirectoryLink[];
}

/** How a link is drawn: the caller's own, so preview links stay inert. */
export type DirectoryLinkRenderer = (
  href: string,
  className: string,
  children: ReactNode,
  style?: CSSProperties,
) => ReactNode;

const categoryPath = (category: StorefrontCategory) =>
  `/categories/${encodeURIComponent(category.slug)}`;

const byPosition = (a: StorefrontCategory, b: StorefrontCategory) => a.position - b.position;

/**
 * The groups a section draws, from its own blocks or the shop's categories.
 *
 * A category tree with no sub-categories anywhere is one group of every
 * category, so a flat catalog still gets its directory rather than a list of
 * headings with nothing under them.
 */
export function linkDirectoryGroups(
  section: LinkDirectorySection,
  categories: StorefrontCategory[],
): DirectoryGroup[] {
  if (section.source === 'manual') {
    return section.blocks
      .filter((block) => block.visible)
      .map((block) => ({
        id: block.id,
        heading: block.heading.trim(),
        links: block.links.filter((link) => link.label.trim() && link.href.trim().startsWith('/')),
      }))
      .filter((group) => group.heading || group.links.length > 0);
  }

  const tops = categories.filter((category) => !category.parent_id).sort(byPosition);
  const childrenOf = (id: string) =>
    categories.filter((category) => category.parent_id === id).sort(byPosition);
  const grouped = tops
    .map((top) => ({
      id: top.id,
      heading: top.name,
      headingHref: categoryPath(top),
      links: childrenOf(top.id).map((child) => ({ label: child.name, href: categoryPath(child) })),
    }))
    .filter((group) => group.links.length > 0);
  if (grouped.length > 0) return grouped;

  const all = [...categories].sort(byPosition);
  if (all.length === 0) return [];
  return [
    {
      id: 'all-categories',
      heading: '',
      links: all.map((category) => ({ label: category.name, href: categoryPath(category) })),
    },
  ];
}

const SEPARATORS: Record<LinkDirectorySection['separator'], string> = {
  pipe: '|',
  comma: ',',
  dot: '·',
};

const COLUMNS: Record<number, string> = {
  1: 'columns-1',
  2: 'columns-1 md:columns-2',
  3: 'columns-1 md:columns-2 lg:columns-3',
};

const MUTED: CSSProperties = { color: 'var(--storefront-muted)' };

export function LinkDirectoryView({
  section,
  groups,
  heading,
  renderLink,
}: {
  section: LinkDirectorySection;
  groups: DirectoryGroup[];
  /** The section's own heading, drawn by the caller. */
  heading?: ReactNode;
  renderLink: DirectoryLinkRenderer;
}) {
  const separator = SEPARATORS[section.separator] ?? '|';
  const comma = section.separator === 'comma';
  const muted = section.heading_style === 'muted';
  const headingClass = muted ? 'font-medium' : 'font-bold';
  const headingStyle = muted ? MUTED : undefined;
  const label = section.title.trim() || undefined;

  return (
    <Container>
      {heading}
      <nav
        aria-label={label}
        className={cn(
          COLUMNS[section.columns] ?? COLUMNS[1],
          'gap-8 leading-relaxed',
          section.text_size === 'sm' ? 'text-sm' : 'text-xs',
        )}
        data-link-directory
      >
        {groups.map((group) => (
          <div key={group.id} className="mb-3 break-inside-avoid" data-link-group>
            {group.heading ? (
              <h3 className={cn(headingClass, 'mb-0.5 text-[1em]')} style={headingStyle}>
                {group.headingHref
                  ? renderLink(group.headingHref, 'hover:underline', group.heading)
                  : group.heading}
              </h3>
            ) : null}
            <ul className="inline" aria-label={group.heading || undefined}>
              {group.links.map((link, index) => (
                // The trailing space is where a long run wraps on a phone.
                <li key={`${link.href}-${index}`} className="inline break-words">
                  {renderLink(link.href, 'hover:underline focus-visible:underline', link.label, MUTED)}
                  {index < group.links.length - 1 ? (
                    <span aria-hidden="true" className={comma ? undefined : 'ms-1.5'} style={MUTED}>
                      {separator}
                    </span>
                  ) : null}{' '}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
    </Container>
  );
}
